import React, { useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  Box,
  Chip,
  Paper,
  CircularProgress,
  Divider,
  Grid,
  Icon,
  Tabs,
  Tab,
  MenuItem
} from '@mui/material';
import { keyframes } from '@mui/system';
import { useApiCall } from '../utils/hooks';
import { predictionApi } from '../utils/api';

// Move modelOptions outside component to prevent recreation on every render
const modelOptions = [
  { label: 'Claude 3.5 Sonnet v2', value: 'claude-3-5-sonnet-v2@20241022' },
  { label: 'Claude 3.7 Sonnet', value: 'claude-3-7-sonnet@20250219' },
  { label: 'Claude Sonnet 4', value: 'claude-sonnet-4@20250514' },
  { label: 'Claude Sonnet 4.5', value: 'claude-sonnet-4-5@20250929' }
];

// Define the blink animation using Material-UI keyframes
const blinkAnimation = keyframes`
  0%, 50% { 
    opacity: 1; 
  }
  51%, 100% { 
    opacity: 0; 
  }
`;

// Move TabPanel outside component to prevent recreation
const TabPanel = ({ children, value, index }) => {
  return (
    <Box role="tabpanel" hidden={value !== index}>
      {value === index && children}
    </Box>
  );
};

function AskPDF() {
  const [result, setResult] = useState(null);
  const [query, setQuery] = useState('');
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [pdfLinks, setPdfLinks] = useState('');
  const [aiResponse, setAiResponse] = useState('AI response will appear here...');
  const [streaming, setStreaming] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [model, setModel] = useState('claude-sonnet-4-5@20250929');
  const [memoryUuid, setMemoryUuid] = useState(null);
  
  const { loading, execute } = useApiCall();

  // Helper function to extract memory UUID from response
  const extractMemoryUuid = (data) => {
    if (typeof data === 'string' && data.includes('memoryUuid')) {
      // Try to extract from streaming response
      const memoryMatch = data.match(/"memoryUuid":\s*"([^"]+)"/);
      if (memoryMatch) {
        return memoryMatch[1];
      }
    }
    
    if (typeof data === 'object') {
      // Try different possible locations in JSON response
      if (data.memoryUuid) {
        return data.memoryUuid;
      }
      if (data.predictions && data.predictions[0] && data.predictions[0].memoryUuid) {
        return data.predictions[0].memoryUuid;
      }
      if (data.data && data.data.memoryUuid) {
        return data.data.memoryUuid;
      }
      // Also check for legacy memory_uuid format
      if (data.memory_uuid) {
        return data.memory_uuid;
      }
      if (data.predictions && data.predictions[0] && data.predictions[0].memory_uuid) {
        return data.predictions[0].memory_uuid;
      }
    }
    
    return null;
  };

  const parseStreamingResponse = (streamingData) => {
    setAiResponse('');
    let accumulatedResponse = '';
    
    // Split by double newlines to get individual data chunks
    const chunks = streamingData.split('\n\n').filter(chunk => chunk.trim());
    
    chunks.forEach((chunk, index) => {
      if (chunk.startsWith('data:')) {
        try {
          const jsonStr = chunk.replace('data:', '').trim();
          const data = JSON.parse(jsonStr);
          
          // Check if this is a text delta chunk
          if (data.type === 'response.text_delta' && data.delta && data.delta.output) {
            // Add delay to simulate typing
            setTimeout(() => {
              accumulatedResponse += data.delta.output;
              setAiResponse(accumulatedResponse);
            }, index * 50); // 50ms delay between chunks
          }
          
          // Handle completed response - only stop streaming, don't override the accumulated text
          if (data.type === 'response.completed') {
            setTimeout(() => {
              setStreaming(false);
            }, (index + 1) * 50);
          }
        } catch (error) {
          console.error('Error parsing streaming chunk:', error);
        }
      }
    });
    
    // Extract memory UUID from final streaming response
    const extractedMemoryUuid = extractMemoryUuid(streamingData);
    if (extractedMemoryUuid && !memoryUuid) {
      console.log(`💾 Extracted memory UUID from streaming response: ${extractedMemoryUuid}`);
      setMemoryUuid(extractedMemoryUuid);
    }
  };

  const testPrediction = async () => {
    setResult(null);
    setAiResponse('●●●');
    setStreaming(true);
    
    try {
      let data;

      // Use follow-up endpoint if we have a memory UUID, otherwise use the main endpoint
      if (memoryUuid) {
        console.log(`🧠 Using existing memory UUID for follow-up: ${memoryUuid}`);
        
        const followUpRequest = {
          query: query,
          memoryUuid: memoryUuid,
          model: model
        };
        
        data = await execute(() => predictionApi.followUp(followUpRequest));
      } else {
        console.log(`📄 First call - including PDF links and product info`);
        
        const requestBody = {
          batch: [{
            query: query,
            id: "test-product",
            model: model,
            name: productName || "Haiku Coastal 52 Universal Mount 3 Blade Outdoor All Weather Fan with LED Light Kit",
            description: productDescription || "Meet the Haiku Coastal: the best performing, most durable marine grade fan on the market.",
            pdfLinks: pdfLinks.split('\n').filter(link => link.trim()).map(link => link.trim())
          }],
          useCaseConfig: {
            domain: "retail"
          }
        };

        data = await execute(() => predictionApi.predict(requestBody));
      }
      
      setResult({
        status: 200,
        data: data,
        success: true
      });

      // Check if response is streaming data format
      if (typeof data === 'string' && data.includes('data:{')) {
        parseStreamingResponse(data);
      } else if (data.predictions && data.predictions.length > 0 && data.predictions[0].response) {
        // Handle regular JSON response
        setAiResponse(data.predictions[0].response);
        setStreaming(false);
        
        // Extract memory UUID from JSON response if this was the first call
        if (!memoryUuid) {
          const extractedMemoryUuid = extractMemoryUuid(data);
          if (extractedMemoryUuid) {
            console.log(`💾 Extracted memory UUID from JSON response: ${extractedMemoryUuid}`);
            setMemoryUuid(extractedMemoryUuid);
          }
        }
      } else if (data.response) {
        setAiResponse(data.response);
        setStreaming(false);
      } else {
        setAiResponse('Response received - check JSON output for details');
        setStreaming(false);
      }

    } catch (error) {
      setResult({
        error: error.message,
        success: false
      });
      setAiResponse(`Error: ${error.message}`);
      setStreaming(false);
    }
  };

  const resetConversation = () => {
    setMemoryUuid(null);
    setAiResponse('AI response will appear here...');
    setResult(null);
    console.log('🔄 Conversation reset - next call will be treated as first call');
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Add useCallback to prevent function recreation
  const handleQueryChange = useCallback((e) => {
    setQuery(e.target.value);
  }, []);

  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', p: 3 }}>
      {/* Header */}
      <Card sx={{ mb: 2, flexShrink: 0 }}>
        <CardContent sx={{ pb: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
            Ask PDF
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Ask questions about PDF documents using AI - get intelligent responses about products and their documentation.
          </Typography>
          {memoryUuid && (
            <Box sx={{ mt: 1 }}>
              <Chip 
                label={`🧠 Memory Session Active`}
                color="primary" 
                size="small"
                sx={{ mr: 1 }}
              />
              <Button 
                variant="outlined" 
                size="small" 
                onClick={resetConversation}
                sx={{ fontSize: '0.75rem', py: 0.5, px: 1 }}
              >
                Reset Conversation
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card sx={{ mb: 2, flexShrink: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Test API" />
            <Tab label="API Specs" />
          </Tabs>
        </Box>
      </Card>

      {/* Tab Content */}
      <TabPanel value={tabValue} index={0}>
        {/* Test API Tab Content */}
        <Grid container spacing={3} sx={{ flex: 1, minHeight: 0 }}>
          {/* Left Column - Input Form */}
          <Grid item xs={12} md={6} lg={5} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 400, md: 500 } }}>
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Query Parameters
                </Typography>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    select
                    fullWidth
                    label="Model"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    variant="outlined"
                  >
                    {modelOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    label="Your Question"
                    multiline
                    rows={2}
                    value={query}
                    onChange={handleQueryChange}
                    placeholder="What are the key features?"
                    variant="outlined"
                    // Add these props to prevent focus loss
                    InputProps={{
                      onFocus: (e) => e.target.select && e.target.select()
                    }}
                    // Ensure the component doesn't lose focus
                    autoComplete="off"
                  />
                  <TextField
                    fullWidth
                    label="Product Name"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder="Product Name"
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label="Product Description"
                    multiline
                    rows={3}
                    value={productDescription}
                    onChange={(e) => setProductDescription(e.target.value)}
                    placeholder="Enter product description..."
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label="PDF Links (one per line)"
                    multiline
                    rows={2}
                    value={pdfLinks}
                    onChange={(e) => setPdfLinks(e.target.value)}
                    placeholder="https://example.com/doc1.pdf"
                    variant="outlined"
                  />
                  <Button
                    variant="contained"
                    size="large"
                    onClick={testPrediction}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <Icon>psychology</Icon>}
                    sx={{ mt: 'auto', py: 1.5 }}
                    fullWidth
                  >
                    {loading ? 'Processing...' : (memoryUuid ? 'Continue Conversation' : 'Ask PDF')}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Response */}
          <Grid item xs={12} md={6} lg={7} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 400, md: 500 } }}>
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    AI Response
                  </Typography>
                  {memoryUuid && (
                    <Chip 
                      label={`Memory: ${memoryUuid.substring(0, 8)}...`}
                      color="primary" 
                      size="small"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  )}
                </Box>
                <Paper sx={{ 
                  flex: 1,
                  p: 2, 
                  bgcolor: streaming ? 'action.hover' : 'grey.50',
                  border: streaming ? '2px solid' : '1px solid',
                  borderColor: streaming ? 'primary.main' : 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'auto'
                }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.6,
                      flex: 1
                    }}
                  >
                    {aiResponse}
                  </Typography>
                  {streaming && (
                    <Box 
                      component="span" 
                      sx={{ 
                        animation: `${blinkAnimation} 1s infinite`,
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontSize: '1rem',
                        lineHeight: 1.6
                      }}
                    >
                      |
                    </Box>
                  )}
                </Paper>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Full API Response - Collapsible */}
        {result && (
          <Card sx={{ mt: 2, flexShrink: 0 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Full API Response
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {result.error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Error: {result.error}
                </Alert>
              ) : (
                <>
                  <Typography variant="body2" gutterBottom>
                    Status: <Chip 
                      label={result.status} 
                      color={result.success ? 'success' : 'error'} 
                      size="small" 
                    />
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', mt: 1, maxHeight: 300, overflow: 'auto' }}>
                    <pre style={{ 
                      margin: 0, 
                      fontFamily: 'monospace', 
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all'
                    }}>
                      {JSON.stringify(result.data, null, 2)}
                    </pre>
                  </Paper>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* API Specs Tab Content */}
        <Card sx={{ mt: 0 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              API Specifications
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Chip label="POST" color="warning" size="small" sx={{ mr: 2 }} />
                <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                  /api/ask-pdf
                </Typography>
              </Box>
              
              <Typography variant="body1" paragraph>
                Ask questions about PDF documents using AI - get intelligent responses about products and their documentation.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Request Body
            </Typography>
            <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 3, overflow: 'auto' }}>
              <pre style={{ 
                margin: 0, 
                fontFamily: 'monospace', 
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap'
              }}>
{`{
  "batch": [
    {
      "name": "Product Name",
      "description": "Product description text",
      "pdfLinks": [
        "https://example.com/document1.pdf",
        "https://example.com/document2.pdf"
      ],
      "query": "Your question about the PDF content",
      "id": "unique-product-id",
      "model": "claude-sonnet-4-5@20250929"
    }
  ],
  "useCaseConfig": {
    "domain": "retail"
  }
}`}
              </pre>
            </Paper>

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Request Parameters
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>batch</strong> (array, required): Array of prediction requests
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, ml: 2 }}>
                • <strong>name</strong> (string): Product or item name
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, ml: 2 }}>
                • <strong>description</strong> (string): Product description
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, ml: 2 }}>
                • <strong>pdfLinks</strong> (array): URLs to PDF documents
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, ml: 2 }}>
                • <strong>query</strong> (string): Question to ask about the PDF content
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, ml: 2 }}>
                • <strong>model</strong> (string): AI model to use for processing
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, ml: 2 }}>
                • <strong>id</strong> (string): Unique identifier for the request
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>useCaseConfig</strong> (object): Configuration settings
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, ml: 2 }}>
                • <strong>domain</strong> (string): Domain context (e.g., "retail")
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Response Format
            </Typography>
            <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 3, overflow: 'auto' }}>
              <pre style={{ 
                margin: 0, 
                fontFamily: 'monospace', 
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap'
              }}>
{`{
  "predictions": [
    {
      "response": "AI-generated response to your question",
      "id": "unique-product-id",
      "metadata": {
        "processedPdfs": 2,
        "processingTime": "1.5s",
        "model": "claude-sonnet-4-5@20250929"
      }
    }
  ],
  "status": "success",
  "timestamp": "2024-01-01T00:00:00Z"
}`}
              </pre>
            </Paper>

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Response Fields
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>predictions</strong> (array): Array of prediction results
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, ml: 2 }}>
                • <strong>response</strong> (string): AI-generated answer to the query
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, ml: 2 }}>
                • <strong>id</strong> (string): Request identifier
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, ml: 2 }}>
                • <strong>metadata</strong> (object): Additional processing information
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>status</strong> (string): Request processing status
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>timestamp</strong> (string): Response timestamp
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Error Responses
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Chip label="400" color="error" size="small" sx={{ mr: 1 }} />
              <Typography variant="body2" component="span">Bad Request - Invalid request format</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Chip label="401" color="error" size="small" sx={{ mr: 1 }} />
              <Typography variant="body2" component="span">Unauthorized - Invalid or missing authentication</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Chip label="429" color="error" size="small" sx={{ mr: 1 }} />
              <Typography variant="body2" component="span">Too Many Requests - Rate limit exceeded</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Chip label="500" color="error" size="small" sx={{ mr: 1 }} />
              <Typography variant="body2" component="span">Internal Server Error - Processing failed</Typography>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>
    </Box>
  );
}

export default AskPDF;